/**
 * Effectifs d'une classe par âge et par genre.
 *
 * Un rapport d'ensemble, pas une liste : une ligne par âge présent, avec le
 * nombre d'élèves et la répartition garçons / filles. Les fiches
 * individuelles sont déjà dans le tableau de la classe — les redonner ici
 * noierait la vue d'ensemble qu'on vient y chercher.
 *
 * Seuls les âges réellement présents apparaissent : afficher chaque année de
 * 2 à 20 remplirait le rapport de lignes à zéro.
 */
import { ageOf } from "@/utils/studentAge";

/**
 * Genre normalisé d'un élève : "M", "F", ou "" si l'information manque.
 *
 * La valeur vient soit du formulaire, qui n'écrit que "M" ou "F", soit d'un
 * import de tableur où la colonne est saisie à la main — on y trouve aussi
 * bien « Masculin » que « f ». On ne retient donc que l'initiale, en
 * majuscule.
 */
export function genderOf(student) {
  const initial = String((student || {}).gender || "")
    .trim()
    .charAt(0)
    .toUpperCase();
  return initial === "M" || initial === "F" ? initial : "";
}

function emptyGroup(age) {
  return { age, total: 0, boys: 0, girls: 0, unknownGender: 0 };
}

/**
 * Regroupe les élèves par âge.
 *
 * @param {Array} students
 * @returns {{groups: Array, totals: object, withoutDob: number}}
 *   `groups` est trié du plus jeune au plus âgé. `withoutDob` compte à part
 *   les élèves sans date de naissance exploitable : leur âge est inconnu, et
 *   les ranger dans une tranche quelconque faussserait le rapport. Les taire
 *   serait pire — la somme des lignes ne tomberait plus sur l'effectif.
 */
export function buildAgeBreakdown(students) {
  const list = (students || []).filter(Boolean);
  const byAge = new Map();
  const totals = { total: 0, boys: 0, girls: 0, unknownGender: 0 };
  let withoutDob = 0;

  list.forEach((student) => {
    const gender = genderOf(student);
    totals.total += 1;
    if (gender === "M") totals.boys += 1;
    else if (gender === "F") totals.girls += 1;
    else totals.unknownGender += 1;

    const age = ageOf(student.dob);
    if (age === null) {
      withoutDob += 1;
      return;
    }

    if (!byAge.has(age)) byAge.set(age, emptyGroup(age));
    const group = byAge.get(age);
    group.total += 1;
    if (gender === "M") group.boys += 1;
    else if (gender === "F") group.girls += 1;
    else group.unknownGender += 1;
  });

  const groups = [...byAge.values()].sort((left, right) => left.age - right.age);

  return { groups, totals, withoutDob };
}

/**
 * Part d'un effectif dans le total, en pourcentage entier.
 *
 * Sert aux barres du rapport. Un total nul rend 0 plutôt qu'une division
 * impossible.
 */
export function shareOf(count, total) {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}
